import type { AuditAction, AuditModule } from '../types/auditLog';

interface ResolvedAuditEvent {
  module: AuditModule;
  subModule?: string;
  action: AuditAction;
  targetType?: string;
}

/** URL patterns that produce too much noise and should never be logged. */
const SKIP_PATTERNS = [
  /\/shared\//,             // all lookup/dropdown calls
  /\/admin\/audit-logs/,    // avoid infinite logging loop
  /\/auth\/refresh/,
  /\/auth\/admin\/login/,
  /\/admin\/tasks\/.*\/history/,
  /\/admin\/dashboard\/stats/,
  /\/admin\/dashboard\/activity-feed/,
  /\/admin\/dashboard\/alerts/,
  /\/admin\/dashboard\/upcoming-birthdays$/,
  /\/admin\/dashboard\/tasks/,
  /\/admin\/tasks\/meta\//,
  /\/admin\/task-types/,
  /\/admin\/task-labels\/palette/,
  /\/admin\/employee\/meta\//,
  /\/admin\/employee\/search/,
  /\/admin\/leads\/search/,   // re-checked below for LIST_VIEW
  /\/admin\/suggestions\/.*\/search/,
  /\/admin\/suggestions\/pending\/search/,
];

/**
 * Map an HTTP method + relative URL to an audit event descriptor.
 * Returns null when the request should not be logged.
 */
export function resolveAuditEvent(
  method: string,
  url: string
): ResolvedAuditEvent | null {
  const m = method.toUpperCase();

  // Skip noisy/internal endpoints
  if (SKIP_PATTERNS.some(p => p.test(url))) {
    // Special-case: leads search IS the list view
    if (m === 'GET' && /\/admin\/leads\/search/.test(url)) {
      return { module: 'leads', action: 'LIST_VIEW' };
    }
    return null;
  }

  // ── AUTH ──────────────────────────────────────────────────────────────────
  if (url.includes('/auth/logout'))
    return { module: 'auth', action: 'LOGOUT' };
  if (url.includes('/auth/admin/forgot-password'))
    return { module: 'auth', action: 'PASSWORD_RESET_REQUEST' };
  if (url.includes('/auth/admin/reset-password'))
    return { module: 'auth', action: 'PASSWORD_RESET_SUCCESS' };

  // ── USERS — specific actions first ───────────────────────────────────────
  if (/\/admin\/user\/block\//.test(url))
    return { module: 'users', action: 'BLOCK', targetType: 'user' };
  if (/\/admin\/user\/unblock\//.test(url))
    return { module: 'users', action: 'UNBLOCK', targetType: 'user' };
  if (/\/admin\/user\/hide\//.test(url))
    return { module: 'users', action: 'HIDE', targetType: 'user' };
  if (/\/admin\/user\/unhide\//.test(url))
    return { module: 'users', action: 'UNHIDE', targetType: 'user' };
  if (/\/admin\/user\/.*\/send-invite/.test(url))
    return { module: 'users', action: 'UPDATE', targetType: 'user' };
  if (url.includes('/admin/user/assign-plan'))
    return { module: 'users', action: 'PLAN_ASSIGN', targetType: 'user' };
  if (url.includes('/admin/user/bulk/export'))
    return { module: 'users', action: 'EXPORT', targetType: 'user' };

  // Invoice download (more specific, before generic invoice check)
  if (/\/admin\/user\/.*\/invoices\/download/.test(url))
    return { module: 'invoices', action: 'INVOICE_DOWNLOAD', targetType: 'invoice' };
  // Invoice list per user
  if (m === 'GET' && /\/admin\/user\/.*\/invoices$/.test(url))
    return { module: 'invoices', action: 'INVOICE_VIEW', targetType: 'invoice' };

  // All user plans list (Invoices page)
  if (m === 'GET' && url.includes('/admin/user/all-plans'))
    return { module: 'invoices', action: 'LIST_VIEW' };

  // Internal notes
  if (m === 'POST' && /\/admin\/user\/.*\/internal-notes$/.test(url))
    return { module: 'users', subModule: 'notes', action: 'NOTE_CREATE', targetType: 'note' };
  if ((m === 'PUT' || m === 'PATCH') && /\/admin\/user\/.*\/internal-notes\//.test(url))
    return { module: 'users', subModule: 'notes', action: 'NOTE_UPDATE', targetType: 'note' };
  if (m === 'DELETE' && /\/admin\/user\/.*\/internal-notes\//.test(url))
    return { module: 'users', subModule: 'notes', action: 'NOTE_DELETE', targetType: 'note' };

  // User CRUD
  if (m === 'POST' && url === '/admin/user/search')
    return { module: 'users', action: 'LIST_VIEW' };
  if (m === 'GET' && url.endsWith('/admin/user/all'))
    return { module: 'users', action: 'LIST_VIEW' };
  if (m === 'GET' && /\/admin\/user\/[^/]+$/.test(url))
    return { module: 'users', action: 'PROFILE_VIEW', targetType: 'user' };
  if (m === 'POST' && url === '/admin/user')
    return { module: 'users', action: 'CREATE', targetType: 'user' };
  if ((m === 'PUT' || m === 'PATCH') && /\/admin\/user\/[^/]+$/.test(url))
    return { module: 'users', action: 'UPDATE', targetType: 'user' };
  if (m === 'DELETE' && /\/admin\/user\/[^/]+$/.test(url))
    return { module: 'users', action: 'DELETE', targetType: 'user' };

  // ── SUGGESTIONS ──────────────────────────────────────────────────────────
  if (/\/admin\/suggestions\/.*\/send/.test(url))
    return { module: 'users', subModule: 'suggestions', action: 'SUGGESTION_SEND', targetType: 'user' };
  if (/\/admin\/suggestions\/.*\/approve/.test(url))
    return { module: 'users', subModule: 'suggestions', action: 'SUGGESTION_APPROVE', targetType: 'user' };
  if (/\/admin\/suggestions\/.*\/reject/.test(url))
    return { module: 'users', subModule: 'suggestions', action: 'SUGGESTION_REJECT', targetType: 'user' };

  // ── LEADS ─────────────────────────────────────────────────────────────────
  if (url.includes('/admin/leads/bulk/delete'))
    return { module: 'leads', action: 'BULK_DELETE', targetType: 'lead' };
  if (url.includes('/admin/leads/bulk/export'))
    return { module: 'leads', action: 'BULK_EXPORT', targetType: 'lead' };
  if (url.includes('/admin/leads/bulk/create-task'))
    return { module: 'leads', action: 'TASK_BULK_CREATE', targetType: 'lead' };
  if (url.includes('/admin/leads/import'))
    return { module: 'leads', action: 'BULK_IMPORT', targetType: 'lead' };
  if (m === 'GET' && /\/admin\/leads\/[^/]+$/.test(url))
    return { module: 'leads', action: 'VIEW', targetType: 'lead' };
  if (m === 'POST' && url === '/admin/leads')
    return { module: 'leads', action: 'CREATE', targetType: 'lead' };
  if ((m === 'PUT' || m === 'PATCH') && /\/admin\/leads\/[^/]+$/.test(url))
    return { module: 'leads', action: 'UPDATE', targetType: 'lead' };
  if (m === 'DELETE' && /\/admin\/leads\/[^/]+$/.test(url))
    return { module: 'leads', action: 'DELETE', targetType: 'lead' };

  // ── KANBAN — comments (most specific first) ──────────────────────────────
  if (m === 'POST' && /\/admin\/tasks\/.*\/comments$/.test(url))
    return { module: 'kanban', subModule: 'comments', action: 'COMMENT_ADD', targetType: 'task' };
  if ((m === 'PUT' || m === 'PATCH') && /\/admin\/tasks\/.*\/comments\//.test(url))
    return { module: 'kanban', subModule: 'comments', action: 'COMMENT_EDIT', targetType: 'task' };
  if (m === 'DELETE' && /\/admin\/tasks\/.*\/comments\//.test(url))
    return { module: 'kanban', subModule: 'comments', action: 'COMMENT_DELETE', targetType: 'task' };

  // Task status change
  if (/\/admin\/tasks\/.*\/status/.test(url))
    return { module: 'kanban', subModule: 'tasks', action: 'STATUS_CHANGE', targetType: 'task' };

  // Labels
  if (m === 'POST' && url === '/admin/task-labels')
    return { module: 'kanban', subModule: 'labels', action: 'LABEL_CREATE', targetType: 'label' };
  if ((m === 'PUT' || m === 'PATCH') && /\/admin\/task-labels\/[^/]+$/.test(url))
    return { module: 'kanban', subModule: 'labels', action: 'LABEL_UPDATE', targetType: 'label' };
  if (m === 'DELETE' && /\/admin\/task-labels\/[^/]+$/.test(url))
    return { module: 'kanban', subModule: 'labels', action: 'LABEL_DELETE', targetType: 'label' };

  // Board view
  if (m === 'GET' && url.endsWith('/admin/tasks/board'))
    return { module: 'kanban', action: 'BOARD_VIEW' };

  // Task CRUD
  if (m === 'POST' && url === '/admin/tasks')
    return { module: 'kanban', subModule: 'tasks', action: 'TASK_CREATE', targetType: 'task' };
  if ((m === 'PUT' || m === 'PATCH') && /\/admin\/tasks\/[^/]+$/.test(url))
    return { module: 'kanban', subModule: 'tasks', action: 'TASK_UPDATE', targetType: 'task' };
  if (m === 'DELETE' && /\/admin\/tasks\/[^/]+$/.test(url))
    return { module: 'kanban', subModule: 'tasks', action: 'TASK_DELETE', targetType: 'task' };

  // ── EMPLOYEES ─────────────────────────────────────────────────────────────
  if (m === 'POST' && url === '/admin/branch')
    return { module: 'employees', subModule: 'branches', action: 'BRANCH_CREATE', targetType: 'branch' };
  if (/\/admin\/employee\/.*\/deactivate/.test(url))
    return { module: 'employees', action: 'DEACTIVATE', targetType: 'employee' };
  if (/\/admin\/employee\/.*\/activate/.test(url))
    return { module: 'employees', action: 'ACTIVATE', targetType: 'employee' };
  if (/\/admin\/employee\/.*\/send-invite/.test(url))
    return { module: 'employees', action: 'UPDATE', targetType: 'employee' };
  if (m === 'GET' && url.endsWith('/admin/employee/all'))
    return { module: 'employees', action: 'LIST_VIEW' };
  if (m === 'GET' && /\/admin\/employee\/[^/]+$/.test(url))
    return { module: 'employees', action: 'PROFILE_VIEW', targetType: 'employee' };
  if (m === 'POST' && url === '/admin/employee')
    return { module: 'employees', action: 'CREATE', targetType: 'employee' };
  if ((m === 'PUT' || m === 'PATCH') && /\/admin\/employee\/[^/]+$/.test(url))
    return { module: 'employees', action: 'UPDATE', targetType: 'employee' };

  // ── PLANS — variants first ────────────────────────────────────────────────
  if (m === 'POST' && /\/admin\/plan\/.*\/variants$/.test(url))
    return { module: 'plans', subModule: 'variants', action: 'VARIANT_CREATE', targetType: 'variant' };
  if ((m === 'PUT' || m === 'PATCH') && /\/admin\/plan\/.*\/variants\//.test(url))
    return { module: 'plans', subModule: 'variants', action: 'VARIANT_UPDATE', targetType: 'variant' };
  if (m === 'DELETE' && /\/admin\/plan\/.*\/variants\//.test(url))
    return { module: 'plans', subModule: 'variants', action: 'VARIANT_DELETE', targetType: 'variant' };

  if (m === 'GET' && url.endsWith('/admin/plan'))
    return { module: 'plans', action: 'LIST_VIEW' };
  if (m === 'GET' && /\/admin\/plan\/[^/]+$/.test(url))
    return { module: 'plans', action: 'PLAN_VIEW', targetType: 'plan' };
  if (m === 'POST' && url === '/admin/plans')
    return { module: 'plans', action: 'PLAN_CREATE', targetType: 'plan' };
  if ((m === 'PUT' || m === 'PATCH') && /\/admin\/plan\/[^/]+$/.test(url))
    return { module: 'plans', action: 'PLAN_UPDATE', targetType: 'plan' };
  if (m === 'DELETE' && /\/admin\/plan\/[^/]+$/.test(url))
    return { module: 'plans', action: 'PLAN_DEACTIVATE', targetType: 'plan' };

  // ── DASHBOARD ─────────────────────────────────────────────────────────────
  if (/\/admin\/dashboard\/upcoming-birthdays\/.*\/send-email/.test(url))
    return { module: 'dashboard', action: 'BIRTHDAY_EMAIL_SENT' };

  // File upload
  if (url.includes('/upload-user-image') || url.includes('/upload-user-other-images'))
    return { module: 'users', action: 'PHOTO_UPLOAD', targetType: 'user' };

  return null;
}
